<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import LaisCard from 'dashboard/components-next/LaisCard.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';
import SalesDeliveryEmailEditor from './SalesDeliveryEmailEditor.vue';
import SalesDeliveryRecipientGroup from './SalesDeliveryRecipientGroup.vue';
import {
  deliveryParticipantEmailsValid,
  groupDeliveryRecipients,
  normalizeDeliveryCopies,
} from '../composables/salesDeliveryRecipients';

const props = defineProps({
  participants: { type: Array, default: () => [] },
  copies: { type: Array, default: () => [] },
  checked: { type: String, default: '0' },
  error: { type: String, default: '' },
  disabled: { type: Boolean, default: false },
  savingRecipients: { type: Boolean, default: false },
  saveRecipients: { type: Function, required: true },
});

const emit = defineEmits(['update']);
const { t } = useI18n();
const editorOpen = ref(false);
const groups = computed(() => groupDeliveryRecipients(props.participants));
const copies = computed(() =>
  normalizeDeliveryCopies(props.copies).filter(copy => copy.email)
);
const recipientsComplete = computed(() =>
  deliveryParticipantEmailsValid(props.participants)
);

const saveRecipients = async payload => {
  const saved = await props.saveRecipients(payload);
  if (saved) editorOpen.value = false;
};
</script>

<template>
  <LaisCard prefix-icon="i-lucide-mail-check">
    <template #header>
      <div class="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3
            class="font-redHatDisplay text-lais-heading-md font-semibold text-lais-text"
          >
            {{ t('SALES.DELIVERY.EMAILS.TITLE') }}
          </h3>
          <p class="mt-1 text-sm text-lais-text-muted">
            {{ t('SALES.DELIVERY.EMAILS.DESCRIPTION') }}
          </p>
        </div>
        <LaisPrimaryButton
          variant="ghost"
          icon="i-lucide-pencil"
          :disabled="disabled || savingRecipients"
          @click="editorOpen = true"
        >
          {{ t('SALES.DELIVERY.EMAILS.EDIT') }}
        </LaisPrimaryButton>
      </div>
    </template>
    <div class="grid gap-4">
      <SalesDeliveryRecipientGroup
        v-for="group in groups"
        :key="group.role"
        :group="group"
      />
      <div
        class="rounded-lais-sm border border-lais-border-soft bg-lais-bg-soft p-3"
      >
        <p
          class="text-xs font-bold uppercase tracking-wide text-lais-text-muted"
        >
          {{ t('SALES.DELIVERY.EMAILS.COPIES') }}
        </p>
        <div v-if="copies.length" class="mt-2 grid gap-2">
          <div
            v-for="copy in copies"
            :key="copy.id || copy.email"
            class="flex flex-wrap justify-between gap-2 text-sm"
          >
            <span class="font-semibold text-lais-text">{{
              copy.name || t('SALES.DELIVERY.EMAILS.COPY_NAME')
            }}</span>
            <span class="text-lais-text-muted">{{ copy.email }}</span>
          </div>
        </div>
        <p v-else class="mt-2 text-sm text-lais-text-muted">
          {{ t('SALES.DELIVERY.EMAILS.NO_COPIES') }}
        </p>
      </div>
    </div>
    <label class="mt-4 flex items-start gap-2 text-sm text-lais-text">
      <input
        type="checkbox"
        :checked="checked === '1'"
        class="mt-0.5 size-4 accent-lais-primary"
        :disabled="disabled || savingRecipients || !recipientsComplete"
        @change="emit('update', $event.target.checked ? '1' : '0')"
      />
      <span>
        {{ t('SALES.DELIVERY.EMAILS.CONFIRM') }}
        <span v-if="!recipientsComplete" class="block text-xs text-lais-danger">
          {{ t('SALES.DELIVERY.EMAILS.MISSING') }}
        </span>
      </span>
    </label>
    <p v-if="error" class="mt-2 text-xs text-lais-danger">{{ error }}</p>
    <SalesDeliveryEmailEditor
      :show="editorOpen"
      :participants="participants"
      :copies="props.copies"
      :disabled="disabled"
      :saving="savingRecipients"
      @close="editorOpen = false"
      @save="saveRecipients"
    />
  </LaisCard>
</template>
